// Surprise me — randomized but tasteful starting compositions.
//
// Picks a base generator + an optional second layer + a palette + maybe a
// dither. Constraints prevent ugly outputs (e.g. bayer dither only when
// levels >= 4, palette must exist).

import { mulberry32, pick } from '@/lib/rng';
import { paletteIds } from '@/filters/palette/lospec';
import { makeDefaultLayer } from '@/core/pipeline';
import { replaceState } from '@/core/state';
import type { AppState, Layer } from '@/core/types';
import { INITIAL_STATE } from '@/core/types';
import { GENERATOR_KINDS } from '@/generators';

const BASE_GENERATORS = ['perlin', 'voronoi', 'worley', 'gradient', 'lattice'] as const;

export function surpriseMe(seed?: number): void {
  const rng = mulberry32(seed ?? Math.floor(Math.random() * 0x7fffffff));

  const baseKind = pick(rng, BASE_GENERATORS);
  const base = randomizeLayer(makeDefaultLayer(baseKind, '_'), rng);

  const layers: Omit<Layer, 'id'>[] = [stripId(base)];

  // 30% chance of a second generator overlay
  if (rng() < 0.3) {
    const second = randomizeLayer(makeDefaultLayer(pick(rng, BASE_GENERATORS), '_'), rng);
    layers.push({ ...stripId(second), opacity: 0.3 + rng() * 0.4, blend: rng() < 0.5 ? 'multiply' : 'screen' });
  }

  // Maybe a dither
  if (rng() < 0.5) {
    layers.push({
      kind: 'dither',
      params: {
        algo: pick(rng, ['floyd', 'atkinson', 'bayer4', 'bayer8'] as const),
        levels: 4,
        amount: 1,
      },
      blend: 'source-over',
      opacity: 1,
      enabled: true,
      seed: 0,
    });
  }

  // Always a palette
  const palette = pick(rng, paletteIds());
  layers.push({
    kind: 'palette',
    params: { palette },
    blend: 'source-over',
    opacity: 1,
    enabled: true,
    seed: 0,
  });

  const state: AppState = {
    ...INITIAL_STATE,
    resolution: { w: 128, h: 128 },
    layers: layers.map((l, i) => ({ ...l, id: `s${i}_${Math.floor(rng() * 1e9).toString(36)}` })),
  };
  state.selectedLayerId = state.layers[0]?.id ?? null;
  replaceState(state, true);
}

function stripId(layer: Layer): Omit<Layer, 'id'> {
  const { id: _id, ...rest } = layer;
  void _id;
  return rest;
}

function randomizeLayer(layer: Layer, rng: () => number): Layer {
  // Tweak common params; keep things tasteful.
  const params = { ...layer.params };
  for (const key of Object.keys(params)) {
    const v = params[key];
    if (typeof v === 'number' && key !== 'octaves' && key !== 'persistence') {
      // wiggle scalar params slightly
      const j = (rng() - 0.5) * 0.5;
      params[key] = Math.max(0.01, v * (1 + j));
    }
  }
  // Specific overrides for known keys
  if (layer.kind === 'perlin') {
    params['scale'] = 16 + rng() * 64;
    params['octaves'] = 3 + Math.floor(rng() * 4);
    params['contrast'] = 0.8 + rng() * 1.4;
  } else if (layer.kind === 'voronoi') {
    params['sites'] = 4 + Math.floor(rng() * 32);
    params['hue'] = Math.floor(rng() * 360);
    params['spread'] = 30 + rng() * 100;
    params['edges'] = rng() < 0.5;
  } else if (layer.kind === 'worley') {
    params['sites'] = 8 + Math.floor(rng() * 48);
    params['mode'] = pick(rng, ['f1', 'f2-f1', 'f2'] as const);
  } else if (layer.kind === 'gradient') {
    params['type'] = pick(rng, ['linear', 'radial', 'conic'] as const);
    params['angle'] = Math.floor(rng() * 360);
    params['power'] = 0.5 + rng() * 2;
  } else if (layer.kind === 'lattice') {
    params['shape'] = pick(rng, ['square', 'checker', 'hex', 'triangle', 'lines'] as const);
    params['size'] = 4 + Math.floor(rng() * 24);
  }
  return {
    ...layer,
    params,
    seed: Math.floor(rng() * 0x7fffffff),
  };
}

void GENERATOR_KINDS;
